import { createElement, useCallback, useMemo, useRef } from "react"
import type { ReactElement, RefObject } from "react"

import { QueryClient, type QueryObserverResult, useQueryClient } from "@tanstack/react-query"
import eq from "lodash-es/eq"
import find from "lodash-es/find"
import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"
import isNumber from "lodash-es/isNumber"
import isUndefined from "lodash-es/isUndefined"
import { type Location, type NavigateFunction, useLocation, useNavigate } from "react-router-dom"
import { useShallowCompareEffect, useUpdate, useUpdateEffect } from "react-use"

import { ErrorHint } from "@/3514/components"
import { EPath, PARTICIPANT_ONBOARDING_ROOT_PATH } from "@/3514/constants"
import { type TUserState, useIsNetworkFast, useUser } from "@/3514/hooks"
import { participantCoachingSessionsPagePreloader as participantSessionsPagePreloader } from "@/3514/pages/participant.coaching-sessions"
import { type TProductTypeQuery, coachingSessionsIndividualQueryPrefetcher, useProductTypeQuery } from "@/3514/queries"
import { type TAppDispatcher, useAppDispatcher } from "@/3514/store/app"
import { EProductTypeInternalName, EUserRole } from "@/3514/types"
import { isUserParticipant } from "@/3514/utils"
import { INDEX_URL } from "@/constants"
import { type TEmptyAsyncCallback, type TEmptyCallback, emptyCallback } from "@/shared/types/functions"

import {
    EParticipantOnboardingQueryKey as EQueryKey,
    EParticipantOnboardingStep as EStep
} from "./participant.onboarding.api.config"
import queries, {
    type TParticipantOnboardingStepsInfoQuery as TStepInfoQuery
} from "./participant.onboarding.api.queries.hooks"
import type {
    IParticipantOnboardingStepsInfoQueryModel as IStepInfoQueryModel,
    IParticipantOnboardingStepsInfoQueryDto as IStepQueryDto,
    TParticipantOnboardingMutationPayloadMap as TMutationPayloadMap,
    TParticipantOnboardingMutationResponse as TMutationResponse
} from "./participant.onboarding.api.types"
import { type TParticipantOnboardingStep as TStep } from "./participant.onboarding.ui.types"
import uiUtils from "./participant.onboarding.ui.utils"

const { useStepInfoQuery, getStepQueriesPrefetchersMap } = queries
const {
    getCurrentStepUrlFromQueryModel,
    getAllStepsFromQueryModel,
    getNextStepNameFromAvailableAndPreviousStep: getNextStepName,
    getNextPagePreloader
} = uiUtils

async function participantHeaderLoader(): Promise<void> {
    await import("@/3514/entities/participant.header").then(m => ({ default: m.ParticipantHeaderEntity }))
}
async function participantSessionsLoader(): Promise<void> {
    await import("@/3514/pages/participant.coaching-sessions").then(m => ({
        default: m.ParticipantCoachingSessionsPage
    }))
}

type TParticipantOnboardingStepsState = {
    allSteps: TStep[] | []
    currentStepByNavigation: TStep | -1 | undefined
    currentStepByProgress: TStep | undefined
    nextStep: TStep | undefined
    isCurrentStepLast: boolean
}

function useSteps(): TParticipantOnboardingStepsState {
    const { pathname }: Location = useLocation()

    const { user, isLoaded: isUserLoaded }: TUserState = useUser()

    const { data: onboardingSteps, isSuccess: isOnboardingStepsLoaded }: TStepInfoQuery = useStepInfoQuery({
        enabled: isUserLoaded && isUserParticipant(user) && !user.isOnboardingFinished && user.doesCohortExist
    })

    const allSteps: TStep[] | [] = useMemo(
        (): TStep[] | [] => getAllStepsFromQueryModel(onboardingSteps, isOnboardingStepsLoaded),
        [onboardingSteps, isOnboardingStepsLoaded]
    )

    const currentStepByNavigation: TStep | -1 | undefined = isEmpty(onboardingSteps)
        ? undefined
        : includes<string>([EPath.Participant_OnboardingRedirect(), PARTICIPANT_ONBOARDING_ROOT_PATH], pathname)
          ? -1
          : find<TStep>(allSteps, { url: pathname } as never)

    const currentStepByProgress: TStep | undefined = isEmpty(allSteps)
        ? undefined
        : find<TStep>(allSteps, { isCompleted: false })

    const nextStep: TStep | undefined = isUndefined(currentStepByProgress)
        ? undefined
        : find<TStep>(allSteps, { order: currentStepByProgress.order + 1 })

    const isCurrentStepLast: boolean = isUndefined(nextStep) ? false : eq(nextStep.name, EStep.Final)

    return {
        allSteps,
        currentStepByNavigation,
        currentStepByProgress,
        nextStep,
        isCurrentStepLast
    }
}

type TParticipantOnboardingMutationSuccessHandler<TStep extends EStep = EStep> = (
    response: TMutationResponse,
    vars: TMutationPayloadMap[TStep]
) => Promise<void>
type TParticipantOnboardingMutationSuccessCurried = <TStep extends EStep>(
    middleware?:
        | TEmptyAsyncCallback
        | ((response: TMutationResponse, vars: TMutationPayloadMap[TStep]) => Promise<void>)
) => TParticipantOnboardingMutationSuccessHandler
type TParticipantOnboardingMutationOptions = {
    onSuccess: TParticipantOnboardingMutationSuccessCurried
}
function useMutationOptions(): TParticipantOnboardingMutationOptions {
    const { data: productTypeData }: TProductTypeQuery = useProductTypeQuery()

    const { nextStep }: TParticipantOnboardingStepsState = useSteps()

    const queryClient: QueryClient = useQueryClient()

    const navigate: NavigateFunction = useNavigate()

    const {
        update: updateUser,
        user: { id: userId, areModulesAvailable, modulesShortList }
    }: TUserState<EUserRole.Participant> = useUser<EUserRole.Participant>()

    const isNetworkFast: boolean = useIsNetworkFast()

    const prefetchersMap: Record<EStep, TEmptyAsyncCallback> = getStepQueriesPrefetchersMap(queryClient)

    const appDispatcher: TAppDispatcher = useAppDispatcher()

    const shouldParticipantHeaderBeVisible: boolean = areModulesAvailable && modulesShortList.length > 0

    return useMemo(
        (): TParticipantOnboardingMutationOptions => ({
            onSuccess:
                <TStep extends EStep>(
                    middleware?:
                        | TEmptyAsyncCallback
                        | ((response: TMutationResponse, vars: TMutationPayloadMap[TStep]) => Promise<void>)
                ): TParticipantOnboardingMutationSuccessHandler =>
                async (response: TMutationResponse, vars: TMutationPayloadMap[TStep]): Promise<void> => {
                    await queryClient.setQueryData(
                        EQueryKey.StepInfo(),
                        (prev: IStepQueryDto): IStepQueryDto => ({
                            available_steps: isEmpty(response.data.available_steps)
                                ? prev.available_steps
                                : response.data.available_steps,
                            previous_steps: isEmpty(response.data.previous_steps)
                                ? prev.previous_steps
                                : response.data.previous_steps
                        })
                    )

                    if (middleware) await middleware(response, vars)

                    if (isNetworkFast) await prefetchersMap[nextStep.name]()

                    if (eq(nextStep.name, EStep.Final)) {
                        appDispatcher.eraseAnimations()

                        await updateUser({ onboarding_finished: true })

                        if (isNetworkFast)
                            await coachingSessionsIndividualQueryPrefetcher(
                                { qc: queryClient },
                                { participantId: userId }
                            )

                        if (eq(productTypeData.internalName, EProductTypeInternalName.LeadershipCoaching)) {
                            if (isNetworkFast) {
                                await participantSessionsPagePreloader()

                                await participantSessionsLoader()
                            }

                            await navigate(EPath.Participant_CoachingSessions(), { replace: true })
                        } else {
                            if (isNetworkFast) if (shouldParticipantHeaderBeVisible) await participantHeaderLoader()

                            await navigate(EPath.Participant_Dashboard(), { replace: true })
                        }
                    } else await navigate(nextStep.url, { replace: true })
                }
        }),
        [
            queryClient,
            isNetworkFast,
            userId,
            appDispatcher,
            navigate,
            updateUser,
            nextStep,
            prefetchersMap,
            productTypeData,
            shouldParticipantHeaderBeVisible
        ]
    )
}

type TUseForceRedirectState = { isStepAllowed: boolean }
function useForceRedirect(): TUseForceRedirectState {
    const isAllowed: RefObject<boolean> = useRef<boolean>(false)

    const { user, isLoaded: isUserLoaded }: TUserState<EUserRole.Participant> = useUser<EUserRole.Participant>()

    const { currentStepByNavigation, currentStepByProgress }: TParticipantOnboardingStepsState = useSteps()

    const navigate: NavigateFunction = useNavigate()

    const triggerUpdate: TEmptyCallback = useUpdate()

    useShallowCompareEffect((): TEmptyCallback => {
        if (!isUserLoaded) return emptyCallback

        if (user.isOnboardingFinished) return void navigate(INDEX_URL, { replace: true })

        if (isUndefined(currentStepByProgress)) return emptyCallback

        if (
            isUndefined(currentStepByNavigation) ||
            isNumber(currentStepByNavigation) ||
            !eq(currentStepByNavigation.name, currentStepByProgress.name)
        )
            return void navigate(currentStepByProgress.url, { replace: true })

        isAllowed.current = true
        triggerUpdate()

        return (): void => void (isAllowed.current = false)
    }, [currentStepByProgress, currentStepByNavigation, isUserLoaded, user.isOnboardingFinished])

    return { isStepAllowed: isAllowed.current }
}

function useNextStepLoader(): void {
    const { nextStep }: TParticipantOnboardingStepsState = useSteps()

    useShallowCompareEffect((): void => {
        if (!isUndefined(nextStep)) void getNextPagePreloader(nextStep.name)()
    }, [nextStep])
}

function useSubmitErrorHint({ isVisible }: { isVisible: boolean }): ReactElement {
    const errorHintRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    useUpdateEffect((): void => {
        if (isVisible) errorHintRef.current?.scrollIntoView({ behavior: "smooth" })
    }, [isVisible])

    return createElement(ErrorHint, { ref: errorHintRef, isVisible })
}

function useParticipantOnboardingStepsLoginNavigator(): TEmptyAsyncCallback {
    const { refetch: refetchOnboardingSteps }: TStepInfoQuery = useStepInfoQuery({
        enabled: false
    })

    const navigate: NavigateFunction = useNavigate()

    const isNetworkFast: boolean = useIsNetworkFast()

    return useCallback(async (): Promise<void> => {
        const stepsResult: QueryObserverResult<IStepInfoQueryModel> = await refetchOnboardingSteps()

        if (stepsResult.isSuccess) {
            if (isNetworkFast)
                await getNextPagePreloader(
                    getNextStepName(stepsResult.data.availableSteps, stepsResult.data.previousSteps)
                )()

            const currentPath: typeof EPath | null = getCurrentStepUrlFromQueryModel(stepsResult.data)

            if (!isEmpty(currentPath)) await navigate(currentPath as never, { replace: true })
        }
    }, [refetchOnboardingSteps, isNetworkFast, navigate])
}

export default {
    useSteps,
    useMutationOptions,
    useForceRedirect,
    useNextStepLoader,
    useSubmitErrorHint
}

export { useParticipantOnboardingStepsLoginNavigator }

export type { TParticipantOnboardingStepsState, TParticipantOnboardingMutationOptions }
